import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	EagerLoadedChildren,
	FolderChildrenKind,
	IFolderViewModel,
	LazyLoadedChildren
} from "client/Api/Email/email";
import { ExchangeAccountItem, ExchangeFolderItem } from "../common";

export enum UpdateEmailFoldersModeKind {
	Add = 1,
	Delete = 2,
	Rename = 3
}

export type AddEmailFolder = {
	kind: UpdateEmailFoldersModeKind.Add;
	newFolder: ExchangeFolderItem;
};

export type DeleteEmailFolder = {
	kind: UpdateEmailFoldersModeKind.Delete;
	selectedFolder: IFolderViewModel<LazyLoadedChildren> | undefined;
	wasUnderDeleteFolder: boolean;
};

export type RenameEmailFolder = {
	kind: UpdateEmailFoldersModeKind.Rename;
	newName: string;
};

export type UpdateEmailFoldersMode =
	| AddEmailFolder
	| DeleteEmailFolder
	| RenameEmailFolder;

export function updateTreeStructure(
	currentStructure: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string,
	mode: UpdateEmailFoldersMode
): readonly ExchangeAccountItem[] {
	switch (mode.kind){
		case UpdateEmailFoldersModeKind.Add:
			return addFolder(
				currentStructure,
				smtpAddress,
				folderId,
				mode
			);
		case UpdateEmailFoldersModeKind.Delete:
			return deleteFolder(
				currentStructure,
				smtpAddress,
				folderId,
				mode.wasUnderDeleteFolder
			);
		case UpdateEmailFoldersModeKind.Rename:
			return renameFolder(
				currentStructure,
				smtpAddress,
				folderId,
				mode
			);
		default:
			never(mode);
	}
};

export function convertEagerLoadedFolderToItem(
	smtpAddress: string,
	folder: IFolderViewModel<EagerLoadedChildren>
): ExchangeFolderItem {
	return {
		kind: "folder",
		smtpAddress,
		folder: {
			...folder,
			children: {
				kind: FolderChildrenKind.LazyLoaded,
				hasChildren: folder
					.children
					.children
					.length > 0
			}
		},
		childrenState: {
			status: "success",
			children: folder
				.children
				.children
				.map(
					child => convertEagerLoadedFolderToItem(smtpAddress, child)
				)
		}
	};
}

function addFolder(
	currentStructure: readonly ExchangeAccountItem[],
	smtpAddress: string,
	parentFolderId: string,
	mode: AddEmailFolder
): readonly ExchangeAccountItem[] {
	const result: TryResult<readonly ExchangeAccountItem[]> = updateNode(
		currentStructure,
		smtpAddress,
		parentFolderId,
		(parent): ExchangeFolderItem | null => {
			const folder: IFolderViewModel<LazyLoadedChildren> = {
				...parent.folder,
				children: {
					...parent.folder
						.children,
					hasChildren: true
				}
			};

			if (parent.childrenState.status !== "success") {
				return {
					...parent,
					folder
				};
			}

			return {
				...parent,
				folder,
				childrenState: {
					...parent.childrenState,
					children: [
						...parent.childrenState.children,
						mode.newFolder
					]
				}
			};
		},
		(account: ExchangeAccountItem): ExchangeAccountItem => {
			if (account.isFaulted) {
				return account;
			}

			return {
				...account,
				folders: [
					...account.folders,
					mode.newFolder
				]
			};
		}
	);

	if (!result.wasSuccessful) {
		throw new Error(`There is no data for folder with id ${parentFolderId}`);
	}

	return result.result;
};

export function findDeletedFolderInAccount(
	accountItem: ExchangeAccountItem
): TryResult<IFolderViewModel<LazyLoadedChildren>> {
	if (accountItem.isFaulted) {
		return { wasSuccessful: false };
	}

	const deleteFolder: ExchangeFolderItem | undefined = accountItem
		.folders
		.find(
			x => x
				.folder
				.isDeleted
		);

	if (isNotNil(deleteFolder)) {
		return {
			wasSuccessful: true,
			result: deleteFolder.folder
		};
	}

	return { wasSuccessful: false };
}

function deleteFolder(
	currentStructure: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string,
	wasUnderDeleteFolder: boolean
): readonly ExchangeAccountItem[] {
	let removedFolder: ExchangeFolderItem | null = null!;

	const removeResult: TryResult<readonly ExchangeAccountItem[]> = updateNode(
		currentStructure,
		smtpAddress,
		folderId,
		(folderToRemove) => {
			removedFolder = folderToRemove;
			return null;
		}
	);

	if (!removeResult.wasSuccessful) {
		throw new Error(`Unable to remove folder with id ${folderId} from tree structure`);
	}

	if (wasUnderDeleteFolder) {
		return removeResult.result;
	}

	const account: ExchangeAccountItem | undefined = currentStructure.find(
		x => x.account.emailAddress === smtpAddress
	);

	if (isNil(account)) {
		throw new Error(`Could not find the account for the mailbox id ${smtpAddress}`);
	}

	const deletedFolderResult: TryResult<IFolderViewModel<LazyLoadedChildren>> = findDeletedFolderInAccount(
		account
	);

	if (!deletedFolderResult.wasSuccessful) {
		return removeResult.result;
	}

	/**
	 * After deleting a folder it must be
	 * moved to the deleted items folder if
	 * it is not there
	 */
	return addFolder(
		removeResult.result,
		smtpAddress,
		deletedFolderResult.result.exchangeFolderId,
		{
			kind: UpdateEmailFoldersModeKind.Add,
			newFolder: ensureNotNil(removedFolder)
		}
	);
};

function renameFolder(
	currentStructure: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string,
	mode: RenameEmailFolder
): readonly ExchangeAccountItem[] {
	const renameResult: TryResult<readonly ExchangeAccountItem[]> = updateNode(
		currentStructure,
		smtpAddress,
		folderId,
		(folderToRename) => {
			return {
				...folderToRename,
				folder: {
					...folderToRename.folder,
					name: mode.newName
				}
			};
		}
	);

	if (!renameResult.wasSuccessful) {
		throw new Error(`There is no data for folder with id ${folderId}`);
	}

	return renameResult.result;
}

function updateNode(
	currentStructure: readonly ExchangeAccountItem[],
	smtpAddress: string,
	folderId: string,
	updateFolderCallback: (current: ExchangeFolderItem) => ExchangeFolderItem | null,
	updateAccountCallback?: (account: ExchangeAccountItem) => ExchangeAccountItem
): TryResult<readonly ExchangeAccountItem[]> {
	const accountItem: ExchangeAccountItem | undefined = currentStructure.find(
		x => x.account.emailAddress === smtpAddress
	);

	if (isNil(accountItem)) {
		throw new Error(`Could not find the account for the mailbox id ${smtpAddress}`);
	}

	if (accountItem.isFaulted) {
		throw new Error(`Could not update a faulted account with id ${smtpAddress}`);
	}

	const { account } = accountItem;

	if (isNotNil(account.rootFolderId) && account.rootFolderId.id === folderId) {
		if (isNil(updateAccountCallback)) {
			throw new Error("Current operation does not support updating accounts.");
		}

		return {
			wasSuccessful: true,
			result: replaceAccount(
				currentStructure,
				updateAccountCallback(accountItem)
			)
		};
	}

	const result: TryResult<readonly ExchangeFolderItem[]> = updateFolderRecursively(
		accountItem.folders,
		folderId,
		updateFolderCallback
	);

	if (!result.wasSuccessful) {
		return { wasSuccessful: false };
	}

	return {
		wasSuccessful: true,
		result: replaceAccount(
			currentStructure,
			{
				...accountItem,
				folders: result.result
			}
		)
	};
}

function replaceAccount(
	structure: readonly ExchangeAccountItem[],
	newAccountItem: ExchangeAccountItem
): readonly ExchangeAccountItem[] {
	return structure.map(
		item => item.account.emailAddress === newAccountItem.account.emailAddress
			? newAccountItem
			: item
	);
}

function updateFolderRecursively(
	folders: readonly ExchangeFolderItem[],
	folderId: string,
	updateCallback: (current: ExchangeFolderItem) => ExchangeFolderItem | null
): TryResult<readonly ExchangeFolderItem[]> {
	let hasChanges: boolean = false;
	const newFolders: ExchangeFolderItem[] = [];

	for (const folderItem of folders) {
		if (folderItem.folder.folderId.id === folderId) {
			const updatedFolder: ExchangeFolderItem | null = updateCallback(folderItem);
			if (isNotNil(updatedFolder)) {
				newFolders.push(updatedFolder);
			}
			hasChanges = true;
			continue;
		}

		if (folderItem.childrenState.status !== "success") {
			newFolders.push(folderItem);
			continue;
		}

		const childResult: TryResult<readonly ExchangeFolderItem[]> = updateFolderRecursively(
			folderItem.childrenState.children,
			folderId,
			updateCallback
		);

		if (!childResult.wasSuccessful) {
			newFolders.push(folderItem);
			continue;
		}

		newFolders.push({
			...folderItem,
			childrenState: {
				...folderItem.childrenState,
				children: childResult.result
			}
		});

		hasChanges = true;
	}

	if (hasChanges) {
		return {
			wasSuccessful: true,
			result: newFolders
		};
	}

	return { wasSuccessful: false };
}